import type { SupabaseClient } from "@supabase/supabase-js";

import { getSupabaseAdmin } from "@/lib/supabaseAdmin";

/** Same normalization as `public.normalize_med_name` in SQL (MED_MATCHING_SETUP.md). */
export function normalizeMedNameForLearning(input: string): string {
  return String(input ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tryGetAdmin(): SupabaseClient | null {
  try {
    return getSupabaseAdmin();
  } catch {
    return null;
  }
}

export async function resolveLearnedCorrectName(
  supabaseAdmin: SupabaseClient,
  rawName: string,
): Promise<string | null> {
  const q = rawName?.trim();
  if (!q) return null;
  const { data, error } = await supabaseAdmin.rpc("get_learned_name_correction", {
    input_name: q,
  });
  if (error) return null;
  const rows = Array.isArray(data) ? data : data ? [data] : [];
  const row = rows[0] as { correct_name?: unknown } | undefined;
  const c = row?.correct_name;
  if (typeof c !== "string" || !c.trim()) return null;
  return c.trim();
}

/** Upsert a global name correction (wrong OCR → human truth). Idempotent when names unchanged. */
export async function recordLearnedNameCorrection(args: {
  wrongName: string;
  correctName: string;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  const wrong = args.wrongName?.trim();
  const correct = args.correctName?.trim();
  if (!wrong || !correct) {
    return { ok: false, error: "wrongName and correctName are required" };
  }
  const wn = normalizeMedNameForLearning(wrong);
  const cn = normalizeMedNameForLearning(correct);
  if (!wn || wn === cn) {
    return { ok: false, error: "No meaningful correction after normalization" };
  }

  const admin = tryGetAdmin();
  if (!admin) {
    return { ok: false, error: "Server storage is not configured" };
  }

  const { data: existing, error: readErr } = await admin
    .from("learned_name_corrections")
    .select("confirmations, correct_name")
    .eq("wrong_normalized", wn)
    .maybeSingle();

  if (readErr) {
    return { ok: false, error: readErr.message };
  }

  const nowIso = new Date().toISOString();
  const prevN = typeof existing?.confirmations === "number" ? existing.confirmations : 0;

  if (!existing) {
    const { error: insErr } = await admin.from("learned_name_corrections").insert({
      wrong_normalized: wn,
      correct_name: correct,
      confirmations: 1,
      last_confirmed_at: nowIso,
      created_at: nowIso,
    });
    if (insErr) return { ok: false, error: insErr.message };
    return { ok: true };
  }

  if (String(existing.correct_name) === correct) {
    const { error: upErr } = await admin
      .from("learned_name_corrections")
      .update({
        confirmations: prevN + 1,
        last_confirmed_at: nowIso,
      })
      .eq("wrong_normalized", wn);
    if (upErr) return { ok: false, error: upErr.message };
    return { ok: true };
  }

  const { error: repErr } = await admin
    .from("learned_name_corrections")
    .update({
      correct_name: correct,
      confirmations: 1,
      last_confirmed_at: nowIso,
    })
    .eq("wrong_normalized", wn);
  if (repErr) return { ok: false, error: repErr.message };
  return { ok: true };
}

export async function applyLearnedNameCorrectionsToParsedMeds<T extends { name: string }>(meds: T[]): Promise<T[]> {
  const admin = tryGetAdmin();
  if (!admin) return meds;

  const out: T[] = [];
  for (const med of meds) {
    const learned = await resolveLearnedCorrectName(admin, med.name);
    if (!learned) {
      out.push(med);
      continue;
    }
    out.push({ ...med, name: learned });
  }
  return out;
}
